import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Save as SaveIcon, Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon, Cancel as CancelIcon } from '@mui/icons-material';
import MarkdownEditor from '../Common/MarkdownEditor';
import Editor from '@monaco-editor/react';

const EditConceptBlockDialog = ({
  open,
  blockIndex,
  conceptBlock,
  onClose,
  onSave,
  saving = false
}) => {
  const [editingData, setEditingData] = useState({
    definition: '',
    notes: [],
    theorems: [],
    examples: []
  });
  const [editingExampleIndex, setEditingExampleIndex] = useState(null); // 当前正在编辑的 example 索引

  useEffect(() => {
    if (conceptBlock) {
      setEditingData({
        definition: conceptBlock.definition || '',
        notes: conceptBlock.notes ? [...conceptBlock.notes] : [],
        theorems: conceptBlock.theorems ? conceptBlock.theorems.map(t => ({
          theorem: t.theorem || '',
          proof: t.proof || ''
        })) : [],
        examples: conceptBlock.examples ? [...conceptBlock.examples] : []
      });
    }
  }, [conceptBlock, open]);

  const handleSave = () => {
    if (onSave) {
      onSave({
        definition: editingData.definition || '',
        notes: editingData.notes.filter(note => note.trim()),
        theorems: editingData.theorems.filter(theorem => 
          (theorem.theorem && theorem.theorem.trim()) || (theorem.proof && theorem.proof.trim())
        ),
        examples: editingData.examples || []
      });
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>修改概念块 {blockIndex !== null ? blockIndex + 1 : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Definition */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              定义
            </Typography>
            <MarkdownEditor
              value={editingData.definition}
              onChange={(value) => setEditingData(prev => ({ ...prev, definition: value }))}
              label="定义内容"
              minRows={8}
              maxRows={15}
              showPreview={true}
              showToolbar={true}
              placeholder="输入定义内容..."
              disabled={saving}
            />
          </Box>

          {/* Theorems */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              定理
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {editingData.theorems.map((theorem, idx) => (
                <Paper key={idx} elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      定理 {idx + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const newTheorems = editingData.theorems.filter((_, i) => i !== idx);
                        setEditingData(prev => ({ ...prev, theorems: newTheorems }));
                      }}
                      disabled={saving}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Stack spacing={2}>
                    <MarkdownEditor
                      value={theorem.theorem}
                      onChange={(value) => {
                        const newTheorems = [...editingData.theorems];
                        newTheorems[idx] = { ...newTheorems[idx], theorem: value };
                        setEditingData(prev => ({ ...prev, theorems: newTheorems }));
                      }}
                      label="定理内容"
                      minRows={4}
                      maxRows={8}
                      showPreview={true}
                      showToolbar={true}
                      placeholder="输入定理内容..."
                      disabled={saving}
                    />
                    <MarkdownEditor
                      value={theorem.proof}
                      onChange={(value) => {
                        const newTheorems = [...editingData.theorems];
                        newTheorems[idx] = { ...newTheorems[idx], proof: value };
                        setEditingData(prev => ({ ...prev, theorems: newTheorems }));
                      }}
                      label="证明"
                      minRows={4}
                      maxRows={8}
                      showPreview={true}
                      showToolbar={true}
                      placeholder="输入证明内容..."
                      disabled={saving}
                    />
                  </Stack>
                </Paper>
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingData(prev => ({
                    ...prev,
                    theorems: [...(prev.theorems || []), { theorem: '', proof: '' }]
                  }));
                }}
                disabled={saving}
                sx={{ alignSelf: 'flex-start' }}
              >
                添加定理
              </Button>
            </Box>
          </Box>

          {/* Notes */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Notes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {editingData.notes.map((note, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <MarkdownEditor
                      value={note}
                      onChange={(value) => {
                        const newNotes = [...editingData.notes];
                        newNotes[idx] = value;
                        setEditingData(prev => ({ ...prev, notes: newNotes }));
                      }}
                      label={`Note ${idx + 1}`}
                      minRows={6}
                      maxRows={12}
                      showPreview={true}
                      showToolbar={true}
                      placeholder="输入笔记内容..."
                      disabled={saving}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newNotes = editingData.notes.filter((_, i) => i !== idx);
                      setEditingData(prev => ({ ...prev, notes: newNotes }));
                    }}
                    disabled={saving}
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingData(prev => ({
                    ...prev,
                    notes: [...(prev.notes || []), '']
                  }));
                }}
                disabled={saving}
                sx={{ alignSelf: 'flex-start' }}
              >
                添加 Note
              </Button>
            </Box>
          </Box>

          {/* Examples */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              相关例子
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {editingData.examples.map((example, idx) => {
                const isEditing = editingExampleIndex === idx;
                return (
                  <Paper key={idx} elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        例子 {idx + 1} ({example.question_type || 'unknown'})
                      </Typography>
                      <Box>
                        {!isEditing ? (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setEditingExampleIndex(idx)}
                            disabled={saving}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => setEditingExampleIndex(null)}
                            disabled={saving}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const newExamples = editingData.examples.filter((_, i) => i !== idx);
                            setEditingData(prev => ({ ...prev, examples: newExamples }));
                            if (editingExampleIndex === idx) {
                              setEditingExampleIndex(null);
                            } else if (editingExampleIndex > idx) {
                              setEditingExampleIndex(editingExampleIndex - 1);
                            }
                          }}
                          disabled={saving}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {!isEditing ? (
                      // 预览模式
                      <Stack spacing={2}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          <strong>题目：</strong>{example.question || '(无题目)'}
                        </Typography>
                        {example.code_answer && (
                          <Box>
                            <Typography variant="body2" component="div" sx={{ color: 'text.secondary', mb: 0.5 }}>
                              <strong>代码：</strong>
                            </Typography>
                            <Box component="pre" sx={{ mt: 0.5, p: 1, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', maxHeight: '200px', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {example.code_answer.replace(/```python\n?/g, '').replace(/```\s*$/g, '')}
                            </Box>
                          </Box>
                        )}
                        {example.options && example.options.length > 0 && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            <strong>选项：</strong>{example.options.join(', ')}
                            {example.correct_answer && ` (正确答案: ${example.correct_answer})`}
                          </Typography>
                        )}
                        {example.answer && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            <strong>答案：</strong>{example.answer}
                          </Typography>
                        )}
                        {example.explanation && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            <strong>解释：</strong>{example.explanation}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      // 编辑模式
                      <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>题目类型</InputLabel>
                          <Select
                            value={example.question_type || 'code_tracing'}
                            label="题目类型"
                            onChange={(e) => {
                              const newExamples = [...editingData.examples];
                              newExamples[idx] = { ...newExamples[idx], question_type: e.target.value };
                              setEditingData(prev => ({ ...prev, examples: newExamples }));
                            }}
                            disabled={saving}
                          >
                            <MenuItem value="multiple_choice">选择题</MenuItem>
                            <MenuItem value="code_tracing">代码追踪题</MenuItem>
                            <MenuItem value="code">代码题</MenuItem>
                            <MenuItem value="proof">证明题</MenuItem>
                            <MenuItem value="short_answer">简答题</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <MarkdownEditor
                          value={example.question || ''}
                          onChange={(value) => {
                            const newExamples = [...editingData.examples];
                            newExamples[idx] = { ...newExamples[idx], question: value };
                            setEditingData(prev => ({ ...prev, examples: newExamples }));
                          }}
                          label="题目内容"
                          minRows={3}
                          maxRows={6}
                          showPreview={true}
                          showToolbar={true}
                          placeholder="输入题目内容..."
                          disabled={saving}
                        />
                        
                        {(example.question_type === 'code_tracing' || example.question_type === 'code') && (
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>代码内容</Typography>
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                              <Editor
                                height="200px"
                                language="python"
                                value={example.code_answer ? example.code_answer.replace(/```python\n?/g, '').replace(/```\s*$/g, '') : ''}
                                onChange={(value) => {
                                  const newExamples = [...editingData.examples];
                                  newExamples[idx] = { ...newExamples[idx], code_answer: value || '' };
                                  setEditingData(prev => ({ ...prev, examples: newExamples }));
                                }}
                                theme="light"
                                options={{
                                  minimap: { enabled: false },
                                  fontSize: 13,
                                  lineNumbers: 'on',
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                        
                        {example.question_type === 'multiple_choice' && (
                          <Stack spacing={2}>
                            <Typography variant="subtitle2">选项</Typography>
                            {[0, 1, 2, 3].map((optIdx) => (
                              <TextField
                                key={optIdx}
                                label={`选项 ${String.fromCharCode(65 + optIdx)}`}
                                fullWidth
                                size="small"
                                multiline
                                rows={3}
                                value={example.options?.[optIdx] || ''}
                                onChange={(e) => {
                                  const newExamples = [...editingData.examples];
                                  const newOptions = [...(newExamples[idx].options || ['', '', '', ''])];
                                  newOptions[optIdx] = e.target.value;
                                  newExamples[idx] = { ...newExamples[idx], options: newOptions };
                                  setEditingData(prev => ({ ...prev, examples: newExamples }));
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                                disabled={saving}
                                inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                              />
                            ))}
                            <FormControl fullWidth size="small">
                              <InputLabel>正确答案</InputLabel>
                              <Select
                                value={example.correct_answer || ''}
                                label="正确答案"
                                onChange={(e) => {
                                  const newExamples = [...editingData.examples];
                                  newExamples[idx] = { ...newExamples[idx], correct_answer: e.target.value };
                                  setEditingData(prev => ({ ...prev, examples: newExamples }));
                                }}
                                disabled={saving}
                              >
                                {['A', 'B', 'C', 'D'].map((letter) => (
                                  <MenuItem key={letter} value={letter}>{letter}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Stack>
                        )}
                        
                        {(example.question_type === 'code_tracing' || example.question_type === 'short_answer' || example.question_type === 'proof') && !example.options && (
                          <MarkdownEditor
                            value={example.answer || ''}
                            onChange={(value) => {
                              const newExamples = [...editingData.examples];
                              newExamples[idx] = { ...newExamples[idx], answer: value };
                              setEditingData(prev => ({ ...prev, examples: newExamples }));
                            }}
                            label="答案"
                            minRows={3}
                            maxRows={6}
                            showPreview={true}
                            showToolbar={true}
                            placeholder="输入答案..."
                            disabled={saving}
                          />
                        )}
                        
                        {example.question_type === 'code' && (
                          <Stack spacing={2}>
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>公开测试用例</Typography>
                              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                                <Editor
                                  height="150px"
                                  language="python"
                                  value={example.public_tests || ''}
                                  onChange={(value) => {
                                    const newExamples = [...editingData.examples];
                                    newExamples[idx] = { ...newExamples[idx], public_tests: value || '' };
                                    setEditingData(prev => ({ ...prev, examples: newExamples }));
                                  }}
                                  theme="light"
                                  options={{
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>秘密测试用例</Typography>
                              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                                <Editor
                                  height="150px"
                                  language="python"
                                  value={example.secret_tests || ''}
                                  onChange={(value) => {
                                    const newExamples = [...editingData.examples];
                                    newExamples[idx] = { ...newExamples[idx], secret_tests: value || '' };
                                    setEditingData(prev => ({ ...prev, examples: newExamples }));
                                  }}
                                  theme="light"
                                  options={{
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                  }}
                                />
                              </Box>
                            </Box>
                          </Stack>
                        )}
                        
                        <MarkdownEditor
                          value={example.explanation || ''}
                          onChange={(value) => {
                            const newExamples = [...editingData.examples];
                            newExamples[idx] = { ...newExamples[idx], explanation: value };
                            setEditingData(prev => ({ ...prev, examples: newExamples }));
                          }}
                          label="解释"
                          minRows={2}
                          maxRows={4}
                          showPreview={true}
                          showToolbar={true}
                          placeholder="输入解释..."
                          disabled={saving}
                        />
                      </Stack>
                    )}
                  </Paper>
                );
              })}
              {editingData.examples.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 2 }}>
                  暂无例子。可以在章节页面中使用"添加题目"来添加例子到此概念块。
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose}
          disabled={saving}
        >
          取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {saving ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditConceptBlockDialog;
